import { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import SearchBar, { matchesSearch } from "../components/SearchBar";
import {
  MemberFields, MemberAvatar, MemberPhotoUpload, emptyMember,
  validateMemberForm, stripAadhar, formatAadhar,
} from "../components/MemberFormPieces";
import {
  UsersThree, Plus, Trash, PencilSimple, IdentificationCard, EnvelopeSimple,
  Phone, MapPin, Calendar, Briefcase, LockKey, ArrowClockwise, CopySimple,
  User as UserIcon, CheckCircle, Warning,
} from "@phosphor-icons/react";

const ROLE_META = {
  admin: { label: "Admin", color: "bg-orange-100 text-orange-800" },
  manager: { label: "Project Manager", color: "bg-blue-100 text-blue-800" },
  member: { label: "Team Member", color: "bg-emerald-100 text-emerald-800" },
};

export default function TeamPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [detailOf, setDetailOf] = useState(null); // user object
  const [pendingDelete, setPendingDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [justCreated, setJustCreated] = useState(null); // {email, password}

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/users");
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Auto-refresh every 30 s so newly assigned tasks / new hires appear without a manual reload.
  useEffect(() => {
    const t = setInterval(() => { load(); }, 30_000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const filtered = useMemo(() =>
    users.filter((u) => matchesSearch(search, u.name, u.email, u.designation, u.contact_no)),
    [users, search]
  );

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await http.delete(`/users/${pendingDelete.id}`);
      toast.success(`Removed ${pendingDelete.name}`);
      setPendingDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Layout
      title="Team"
      subtitle={isAdmin ? "Manage staff records — contact, Aadhaar, address, photo & login."
                        : "View your colleagues in the studio."}
      actions={isAdmin && (
        <Button data-testid="new-member-btn" onClick={() => setCreating(true)}
          className="rounded-none h-11 bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs px-5 shadow-brutal-sm">
          <Plus size={14} weight="bold" className="mr-2" /> New Member
        </Button>
      )}
    >
      {users.length > 0 && (
        <div className="mb-5" data-testid="team-search">
          <SearchBar
            value={search}
            onChange={setSearch}
            testId="team-search-input"
            placeholder="Search by name, email, contact or designation…"
          />
        </div>
      )}

      {users.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-12 text-center bg-white">
          <UsersThree size={36} weight="bold" className="mx-auto text-zinc-300" />
          <div className="label-overline text-zinc-500 mt-3">No team members yet</div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((u) => {
          const meta = ROLE_META[u.role] || ROLE_META.member;
          return (
            <button
              key={u.id}
              onClick={() => setDetailOf(u)}
              data-testid={`member-card-${u.id}`}
              className="text-left bg-white border-t-4 border-zinc-900 border-x border-b border-zinc-300 hover:shadow-brutal-sm"
            >
              <div className="p-4 flex items-center gap-3">
                <MemberAvatar user={u} size={54} className="rounded-none" />
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg font-black tracking-tighter text-zinc-900 leading-tight truncate">{u.name}</div>
                  <div className="label-overline text-zinc-500 truncate">{u.designation || "—"}</div>
                </div>
                <span className={`shrink-0 label-overline px-2 py-1 ${meta.color}`}>{meta.label}</span>
              </div>
              <div className="px-4 pb-4 space-y-1.5 text-xs">
                <div className="flex items-center gap-1.5 text-zinc-600 truncate">
                  <EnvelopeSimple size={11} weight="bold" className="text-zinc-400" /> {u.email}
                </div>
                {u.contact_no && (
                  <div className="flex items-center gap-1.5 text-zinc-600">
                    <Phone size={11} weight="bold" className="text-zinc-400" /> {u.contact_no}
                  </div>
                )}
                {u.role === "member" && (
                  <div className="mt-2 flex items-center gap-1.5">
                    {u.profile_complete ? (
                      <span className="label-overline bg-emerald-100 text-emerald-800 px-2 py-0.5 flex items-center gap-1">
                        <CheckCircle size={10} weight="bold" /> Profile complete
                      </span>
                    ) : (
                      <span className="label-overline bg-amber-100 text-amber-800 px-2 py-0.5 flex items-center gap-1">
                        <Warning size={10} weight="bold" /> Profile incomplete
                      </span>
                    )}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {isAdmin && (
        <NewMemberDialog
          open={creating}
          onOpenChange={setCreating}
          onCreated={(rec) => { setJustCreated(rec); load(); }}
        />
      )}

      <MemberDetailDialog
        member={detailOf}
        isAdmin={isAdmin}
        onOpenChange={(v) => !v && setDetailOf(null)}
        onChanged={load}
        onDelete={(m) => { setDetailOf(null); setPendingDelete(m); }}
      />

      <CredentialToastDialog
        rec={justCreated}
        onOpenChange={(v) => !v && setJustCreated(null)}
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal">
          <AlertDialogHeader>
            <div className="label-overline text-red-600 mb-1">Destructive Action</div>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
              Remove {pendingDelete?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              This deletes their account and all tasks assigned to them. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-member"
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono uppercase tracking-widest text-xs">
              Yes, Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

/* -------- New Member -------- */
function NewMemberDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(emptyMember);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({ ...emptyMember }); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const err = validateMemberForm(form);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const { data } = await http.post("/users", {
        ...form,
        aadhar: stripAadhar(form.aadhar),
      });
      toast.success(`${data.name} added`);
      onCreated?.({ id: data.id, name: data.name, login_id: data.login_id, password: data.initial_password });
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="label-overline text-blue-600 mb-1">Onboarding</div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">New Team Member</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="mt-3 space-y-4" data-testid="new-member-form">
          <MemberFields form={form} setForm={setForm} mode="create" />
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 px-3 py-2">
            <strong>Note:</strong> Photo upload happens after the member is created — you can also let them upload it on first login.
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-300">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="submit-new-member"
                    className="rounded-none bg-zinc-900 hover:bg-blue-600 text-white font-mono uppercase tracking-widest text-xs">
              {saving ? "Creating…" : "Create Member"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* -------- Credential Reveal (shown once) -------- */
function CredentialToastDialog({ rec, onOpenChange }) {
  const [copied, setCopied] = useState(false);
  const open = !!rec;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Login: ${rec.login_id}\nPassword: ${rec.password}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Clipboard blocked"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-md" data-testid="credential-dialog">
        <DialogHeader>
          <div className="label-overline text-orange-600 mb-1">Save These · Shown once</div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
            Login for {rec?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-3 grid grid-cols-1 gap-2">
          <Row k="Login ID" v={rec?.login_id} />
          <Row k="Password" v={rec?.password} />
        </div>
        <div className="text-xs text-zinc-500 mt-3">
          Share these with the member. You won&apos;t see this password again — use &quot;Reset Password&quot; later if lost.
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-zinc-300 mt-3">
          <Button type="button" onClick={copy} data-testid="copy-credentials"
                  className="rounded-none bg-zinc-900 hover:bg-blue-600 text-white font-mono uppercase tracking-widest text-xs">
            <CopySimple size={12} weight="bold" className="mr-1.5" />
            {copied ? "Copied ✓" : "Copy Both"}
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)} variant="outline"
                  className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  return (
    <div className="border border-zinc-300 bg-zinc-50 px-3 py-2">
      <div className="label-overline text-zinc-500">{k}</div>
      <div className="font-mono text-sm mt-0.5 break-all">{v}</div>
    </div>
  );
}

/* -------- Member Detail -------- */
function MemberDetailDialog({ member, isAdmin, onOpenChange, onChanged, onDelete }) {
  const [full, setFull] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyMember);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [reveal, setReveal] = useState(null); // {login_id, new_password}
  const [sendingEmail, setSendingEmail] = useState(false);

  const open = !!member;

  const load = useCallback(async () => {
    if (!member?.id) return;
    try {
      const { data } = await http.get(`/users/${member.id}`);
      setFull(data);
      setForm({
        name: data.name || "", email: data.email, password: "", role: data.role,
        contact_no: data.contact_no || "", alt_contact_no: data.alt_contact_no || "",
        father_name: data.father_name || "", date_of_joining: data.date_of_joining || "",
        aadhar: data.aadhar || "", full_address: data.full_address || "",
        designation: data.designation || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  }, [member?.id]);

  useEffect(() => { if (open) { setEditing(false); setReveal(null); load(); } }, [open, load]);

  if (!open) return null;

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        name: form.name, contact_no: form.contact_no, alt_contact_no: form.alt_contact_no,
        father_name: form.father_name, date_of_joining: form.date_of_joining,
        aadhar: stripAadhar(form.aadhar), full_address: form.full_address, designation: form.designation,
      };
      // Admins may re-role a member; only include role if it actually changed.
      if (isAdmin && form.role && form.role !== full?.role) {
        patch.role = form.role;
      }
      await http.patch(`/users/${member.id}`, patch);
      toast.success(patch.role ? `Profile updated · role changed to ${form.role}` : "Profile updated");
      setEditing(false);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const resetPw = async () => {
    setResetting(true);
    try {
      const { data } = await http.post(`/users/${member.id}/reset-password`);
      setReveal(data);
      toast.success("Password reset");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResetting(false); }
  };

  const emailLogin = async () => {
    setSendingEmail(true);
    try {
      const { data } = await http.post(`/users/${member.id}/send-login`);
      toast.success(`Login details emailed to ${data.sent_to}`);
      setReveal(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingEmail(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-3xl max-h-[92vh] overflow-y-auto"
                     data-testid={`member-detail-${member.id}`}>
        <DialogHeader>
          <div className="label-overline text-blue-600 mb-1">Staff Profile</div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
            {full?.name || member.name}
          </DialogTitle>
        </DialogHeader>

        {!full && <div className="label-overline text-zinc-500 py-4">Loading…</div>}

        {full && !editing && (
          <div className="mt-3 space-y-4">
            <div className="flex items-start gap-4">
              <MemberPhotoUpload
                userId={member.id}
                currentPhoto={full.photo}
                onUploaded={(p) => { setFull((f) => ({ ...f, photo: p })); onChanged?.(); }}
                size={120}
              />
              <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Detail label="Role" v={ROLE_META[full.role]?.label || full.role} />
                <Detail label="Designation" v={full.designation} />
                <Detail label="Contact No." v={full.contact_no} />
                <Detail label="Alternate Contact" v={full.alt_contact_no} />
                <Detail label="Father's Name" v={full.father_name} />
                <Detail label="Date of Joining" v={full.date_of_joining} />
                <Detail label="Aadhaar" v={full.aadhar_display || formatAadhar(full.aadhar)} />
                <Detail label="Email / Login ID" v={full.login_id || full.email} />
                <div className="sm:col-span-2">
                  <Detail label="Full Address" v={full.full_address} multiline />
                </div>
              </div>
            </div>

            {isAdmin && (
              <div className="border border-purple-400 bg-purple-50/50 p-3">
                <div className="label-overline text-purple-700 flex items-center gap-1.5"><LockKey size={12} weight="bold" /> Login Credentials</div>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <div className="font-mono text-sm">{full.login_id || full.email}</div>
                  <Button
                    type="button" onClick={resetPw} disabled={resetting}
                    data-testid={`reset-password-btn-${member.id}`}
                    className="rounded-none h-8 bg-purple-700 hover:bg-purple-800 text-white font-mono uppercase tracking-widest text-xs"
                  >
                    <ArrowClockwise size={11} weight="bold" className="mr-1.5" />
                    {resetting ? "Resetting…" : "Reset Password"}
                  </Button>
                  <Button
                    type="button" onClick={emailLogin} disabled={sendingEmail}
                    data-testid={`send-login-btn-${member.id}`}
                    className="rounded-none h-8 bg-blue-600 hover:bg-blue-700 text-white font-mono uppercase tracking-widest text-xs"
                  >
                    {sendingEmail ? "Sending…" : "Email Login Details"}
                  </Button>
                </div>
                {reveal && (
                  <div className="mt-3 border border-emerald-500 bg-emerald-50 p-3">
                    <div className="label-overline text-emerald-700">New password (shown once)</div>
                    <div className="font-mono text-base mt-1" data-testid="revealed-password">{reveal.new_password}</div>
                    <div className="text-xs text-zinc-600 mt-1">Copy this and share with the member. Refresh loses it.</div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-300">
              {isAdmin && (
                <>
                  <Button variant="outline" onClick={() => onDelete(member)} data-testid="delete-member-btn"
                          className="rounded-none border-red-400 text-red-600 font-mono uppercase tracking-widest text-xs hover:bg-red-50">
                    <Trash size={11} weight="bold" className="mr-1" /> Remove
                  </Button>
                  <Button onClick={() => setEditing(true)} data-testid="edit-member-btn"
                          className="rounded-none bg-zinc-900 hover:bg-blue-600 text-white font-mono uppercase tracking-widest text-xs">
                    <PencilSimple size={11} weight="bold" className="mr-1" /> Edit
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {full && editing && (
          <div className="mt-3 space-y-4">
            <MemberFields form={form} setForm={setForm} mode="edit" />
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-300">
              <Button variant="outline" onClick={() => setEditing(false)}
                      className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs">Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="save-member-edit"
                      className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white font-mono uppercase tracking-widest text-xs">
                {saving ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Detail({ label, v, multiline }) {
  return (
    <div>
      <div className="label-overline text-zinc-500 mb-0.5">{label}</div>
      <div className={`text-sm text-zinc-900 ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>
        {v || <span className="text-zinc-400 italic">—</span>}
      </div>
    </div>
  );
}
